import React, { useEffect, useRef, useState } from 'react';
import { InputProps } from '@tlx/atlas';
import { useComposedRefs } from '../../../hooks/useComposedRefs';
import { validate } from '../../../validators/validation';
import { ValidatedProps } from '../ValidatedForm.types';

export type ValidatedFormInputProps = InputProps & React.RefAttributes<HTMLInputElement> & ValidatedProps;
export function useValidatedFormInput({ validator, ref, ...props }: ValidatedFormInputProps) {
    const [errorMessage, setErrorMessage] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const composedRef = useComposedRefs(inputRef, ref);
    const showError = !!errorMessage && !props.disabled;

    useEffect(() => {
        if (inputRef.current) {
            const error = validate(inputRef.current, validator);
            inputRef.current.setCustomValidity(error);
            // Doing it this way prevents the error showing up before the user has interacted with a dependent input field.
            if (error === '') {
                setErrorMessage(error);
            }
        }
        //TODO: This needs to run on every validation-related prop.
    }, [validator, props.required]);

    // event listener for form reset
    useEffect(() => {
        const form = inputRef.current?.form;
        if (form) {
            const resetListener = () => {
                setErrorMessage('');
            };
            form.addEventListener('reset', resetListener);
            return () => {
                form.removeEventListener('reset', resetListener);
            };
        }
    }, []);

    function onFocus(e: React.FocusEvent<HTMLInputElement>) {
        setErrorMessage('');
        props.onFocus?.(e);
    }
    function onChange(e: React.ChangeEvent<HTMLInputElement>) {
        const error = validate(inputRef.current, validator);
        inputRef.current?.setCustomValidity(error);
        props.onChange?.(e);
    }
    function onBlur(e: React.FocusEvent<HTMLInputElement>) {
        const error = validate(inputRef.current, validator);
        inputRef.current?.setCustomValidity(error);
        inputRef.current?.checkValidity();
        props.onBlur?.(e);
    }
    function onInvalid(e: React.FormEvent<HTMLInputElement>) {
        setErrorMessage(inputRef.current?.validationMessage ?? '');
        props.onInvalid?.(e);
    }

    return {
        errorMessage,
        showError,
        inputProps: {
            ...props,
            ref: composedRef,
            onFocus,
            onChange,
            onBlur,
            onInvalid,
        },
    };
}
